// Lib
import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { defer } from 'lodash/fp';

// Singleton
import zoomStateSingleton from '../../canvas/zoom/zoomStateSingleton';
import resizeAnimationSingleton from './ResizeAnimationSingleton';

// Utils
import {
    endElementResizing,
    startElementResizing,
    updateAllowChangingAspectRatio,
    updateResizeScale,
} from './store/resizingActions';
import { hasCommandKeyCode, hasCommandModifier } from '../../utils/keyboard/keyboardUtility';
import { asObject } from '../../../common/utils/immutableHelper';
import {
    getNewResizeScale,
    getGroupClientOrigin,
    getTopAndLeftElements,
    beginAnimationOrchestration,
} from './resizeUtils';
import { getElementId, getXPosition, getYPosition } from '../../../common/elements/utils/elementPropertyUtils';

// Types
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { ElementMeasurement } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsTypes';
import { stopDefaultAndPropagation } from '../../utils/domUtil';
import requestSecondAnimationFrame from '../../../common/utils/lib/requestSecondAnimationFrame';

const abortAndRefresh = (controllerRef: React.MutableRefObject<AbortController>) => {
    controllerRef.current.abort();
    controllerRef.current = new AbortController();
};

type Props = {
    children?: React.ReactNode;
    gridSize: number;
    inList?: string;
    showHandle?: boolean;
    elementId: string;
    element: ImMNElement;
    handleDoubleClick?: (event: React.MouseEvent) => void;
    getContextZoomScale: () => number;
};

const DragStateInitial = {
    initialMeasurements: null,
    startX: 0,
    startY: 0,
    currentX: null,
    currentY: null,
    currentWidth: undefined,
    currentHeight: undefined,
    groupClientOriginPx: { x: 0, y: 0 },
};

type DragState = {
    initialMeasurements: Map<string, number> | null;
    startX: number;
    startY: number;
    currentX: number | null;
    currentY: number | null;
    currentWidth: number | undefined;
    currentHeight: number | undefined;
    groupClientOriginPx?: { x: number; y: number };
};

const useResizeHandlePointerHandlers = (
    props: Props,
    resizingElements: ImMNElement[],
    getCurrentMeasurements: (elementId: string) => Map<string, number>,
    dispatchMeasurementsSet: ({ id, measurements }: { id: string; measurements: ElementMeasurement }) => void,
) => {
    const { elementId, handleDoubleClick } = props;

    const dispatch = useDispatch();

    const dragStateRef = useRef<DragState>(DragStateInitial);
    const pointerControllerRef = useRef<AbortController>(new AbortController());
    const touchControllerRef = useRef<AbortController>(new AbortController());
    const keyControllerRef = useRef<AbortController>(new AbortController());
    const clickControllerRef = useRef<AbortController>(new AbortController());

    const endResizing = () => {
        abortAndRefresh(keyControllerRef);
        abortAndRefresh(pointerControllerRef);
        abortAndRefresh(touchControllerRef);

        dragStateRef.current.currentX = null;
        dragStateRef.current.currentY = null;

        // Use defer to ensure that collisions aren't checked when the resizing has finished
        defer(() => {
            dispatch(endElementResizing());

            const animationCallbacks = resizeAnimationSingleton.getAnimationCallbacks();
            resizeAnimationSingleton.clearAnimationCallbacks();

            if (!animationCallbacks) return; // nothing to animate

            // Wait for two animation frames to ensure the animationTargetSize has been calculated before starting the animations
            requestSecondAnimationFrame(() => {
                beginAnimationOrchestration(resizingElements, animationCallbacks);
            });
        });
    };

    /**
     * If a zoom operation starts while an element resize is occurring, we need to
     * cancel the element resize and clear the state.
     */
    const cancelPointerMoveDragging = () => {
        // Return the measurements back to what they were before the resize started
        dispatchMeasurementsSet({
            id: elementId,
            measurements: asObject(dragStateRef.current.initialMeasurements || {}),
        });

        endResizing();
    };

    const onPointerMove = (event: MouseEvent) => {
        // If we're performing a zoom operation, then don't handle the element resize
        if (zoomStateSingleton.getIsZooming()) return cancelPointerMoveDragging();

        stopDefaultAndPropagation(event);

        dragStateRef.current.currentX = event.clientX;
        dragStateRef.current.currentY = event.clientY;

        const { startX, startY, currentX, currentY, groupClientOriginPx = { x: 0, y: 0 } } = dragStateRef.current;

        const scale = {
            x: getNewResizeScale(startX, currentX, groupClientOriginPx.x),
            y: getNewResizeScale(startY, currentY, groupClientOriginPx.y),
        };

        dispatch(updateResizeScale(scale));
    };

    const onPointerUp = (event: MouseEvent) => {
        stopDefaultAndPropagation(event);
        endResizing();

        // A click event is always fired right after pointerup, so for this single click we want to prevent it
        // bubbling up to the element container which sometimes selects/deselects elements
        document.addEventListener('click', stopDefaultAndPropagation, {
            once: true,
            capture: true,
            signal: clickControllerRef.current.signal,
        });
    };

    const startDragging = (event: Event) => {
        stopDefaultAndPropagation(event);

        abortAndRefresh(pointerControllerRef);

        document.addEventListener('pointermove', onPointerMove, { signal: pointerControllerRef.current.signal });
        document.addEventListener('pointerup', onPointerUp, { signal: pointerControllerRef.current.signal });
    };

    const onTouchStart = (event: React.TouchEvent) => {
        stopDefaultAndPropagation(event);

        // Passive: false is important here, as this allows preventDefault to be called
        const touchEventOptions = { passive: false, signal: touchControllerRef.current.signal };
        document.addEventListener('touchmove', stopDefaultAndPropagation, touchEventOptions);
        document.addEventListener('touchend', () => abortAndRefresh(touchControllerRef), touchEventOptions);
    };

    const onKeyDown = (event: KeyboardEvent) => dispatch(updateAllowChangingAspectRatio(hasCommandKeyCode(event)));
    const onKeyUp = (event: KeyboardEvent) =>
        hasCommandKeyCode(event) && dispatch(updateAllowChangingAspectRatio(false));

    const onPointerDown = (event: React.PointerEvent) => {
        if (event.button === 1 || event.button === 2) return;

        // Track simultaneous pointer events
        stopDefaultAndPropagation(event);

        const initialMeasurements = getCurrentMeasurements && getCurrentMeasurements(elementId);

        if (!initialMeasurements) return;

        dragStateRef.current.initialMeasurements = initialMeasurements;
        dragStateRef.current.currentWidth = initialMeasurements.get('width');
        dragStateRef.current.currentHeight = initialMeasurements.get('height');
        dragStateRef.current.startX = event.clientX;
        dragStateRef.current.startY = event.clientY;

        const { topElement, leftElement } = getTopAndLeftElements(resizingElements);

        dragStateRef.current.groupClientOriginPx = getGroupClientOrigin(
            getElementId(leftElement),
            getElementId(topElement),
        );

        const topLeftElementGridPosition = { x: getXPosition(leftElement), y: getYPosition(topElement) };
        const resizingElementIds = resizingElements.map((el: ImMNElement) => getElementId(el));
        dispatch(startElementResizing(resizingElementIds, topLeftElementGridPosition));

        document.addEventListener('pointermove', startDragging, { signal: pointerControllerRef.current.signal });
        document.addEventListener('pointerup', () => abortAndRefresh(pointerControllerRef), {
            signal: pointerControllerRef.current.signal,
        });

        dispatch(updateAllowChangingAspectRatio(hasCommandModifier(event)));
        document.addEventListener('keydown', onKeyDown, { signal: keyControllerRef.current.signal });
        document.addEventListener('keyup', onKeyUp, { signal: keyControllerRef.current.signal });
    };

    const onDoubleClick = (event: React.MouseEvent) => {
        handleDoubleClick && handleDoubleClick(event);
        cancelPointerMoveDragging();
    };

    useEffect(() => {
        return () => {
            keyControllerRef.current.abort();
            touchControllerRef.current.abort();
            pointerControllerRef.current.abort();
            clickControllerRef.current.abort();
        };
    }, []);

    return { onTouchStart, onPointerDown, onDoubleClick };
};

export default useResizeHandlePointerHandlers;
